/**
 * เหมันต์สะกดธาร (the snow-sword warp chain, render/skillFx.ts and render/effects.ts): the draw-stance
 * gathering frost, the launch into the blink, a rising cut, a level sweep and its wrap, a falling
 * cleave, a lunge, the blade flicked clean and the finishing stance; one motion per warp; and the
 * arms its cuts swing through.
 */
import type { MotionDef, LimbSpec, PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'snowGather'
  | 'snowVanish'
  | 'snowRise'
  | 'snowSweep'
  | 'snowWrap'
  | 'snowCleave'
  | 'snowLunge'
  | 'snowFlick'
  | 'snowClean';

export const POSES: PoseRigs<Poses> = {
  snowGather: () => {
    // The draw-stance: sunk deep over the front knee, the blade drawn back low behind the hip
    // (frost gathers on it), the sword-finger levelled at the foe.
    return {
      o: 7,
      lean: -1,
      tilt: 14,
      headDx: 1,
      f1: -11,
      f2: 7,
      hem: 1,
      sway: -1,
      arm: 'back',
      far: 'fwd',
      face: 'block',
    };
  },
  snowVanish: () => {
    // Launched into the blink: pitched flat out, the back foot leaving the ground, the blade and
    // the far arm streaming behind.
    return {
      o: 6,
      lean: 3,
      tilt: 28,
      headDx: 1,
      f1: -13,
      f2: 8,
      l1: 3,
      hem: 2,
      flare: 2,
      sway: 3,
      arm: 'back',
      far: 'back',
      face: 'atk',
    };
  },
  snowRise: () => {
    // The rising cut carried through: up on the back toes, the body opened, the blade high and
    // forward.
    return {
      o: 1,
      lean: 2,
      tilt: -6,
      f1: -9,
      f2: 6,
      l1: 2,
      hem: 3,
      flare: 2,
      sway: 1.5,
      arm: 'upcut',
      far: 'back',
      face: 'atk',
    };
  },
  snowSweep: () => {
    // The level sweep: a wide low stance, the blade straight out at the foe, the far arm thrown up
    // behind for balance.
    return {
      o: 8,
      lean: 2,
      tilt: 6,
      f1: -11,
      f2: 10,
      hem: 2,
      flare: 2,
      sway: 2,
      arm: 'thrust',
      far: 'up',
      face: 'atk',
    };
  },
  snowWrap: () => {
    // The sweep wrapped round: the blade carried on across the chest, pointing back past the far
    // shoulder, the body turned with it.
    return {
      o: 6,
      lean: 1,
      tilt: 2,
      f1: -11,
      f2: 9,
      hem: 1,
      sway: -1,
      arm: 'wrap',
      far: 'back',
      face: 'atk',
    };
  },
  snowCleave: () => {
    // The falling cleave landed: the deepest lunge, the blade driven down low past the foe.
    return {
      o: 8,
      lean: 4,
      tilt: 24,
      headDx: 1,
      f1: -12,
      f2: 10,
      hem: 1,
      sway: 2.5,
      arm: 'low',
      far: 'up',
      face: 'atk',
    };
  },
  snowLunge: () => {
    // The thrust: the longest bow stance, body, arm and blade in one line through the foe.
    return {
      o: 7,
      lean: 4,
      tilt: 20,
      headDx: 1,
      f1: -12,
      f2: 11,
      hem: 1,
      sway: 2.5,
      arm: 'thrust',
      far: 'back',
      face: 'atk',
    };
  },
  snowFlick: () => {
    // The blade flicked clean: snapped down and out to the side, the body rising out of the lunge,
    // the sword-finger brought in before the chest.
    return {
      o: 4,
      tilt: 6,
      f1: -9,
      f2: 6,
      sway: -1,
      arm: 'flick',
      far: 'clasp',
      face: 'block',
    };
  },
  snowClean: () => {
    // The finishing stance: upright, the blade held low behind, the sword-finger before the chest,
    // the eyes closed.
    return {
      o: 2,
      lean: -1,
      tilt: -2,
      f1: -7,
      f2: 4,
      sway: 0.5,
      arm: 'back',
      far: 'clasp',
      face: 'idle',
      closed: true,
    };
  },
};

export const MOTIONS = [
  // เหมันต์สะกดธาร: one motion per warp of the chain (picked in render/effects.ts), each opening on its
  // own wind-up key; the last cleaves, flicks the blade clean and settles into the finishing stance.
  /** Drawn from the low draw-stance, swept out level, wrapped round across the body. */
  { id: 'snowSweep', keys: ['snowGather', 'snowSweep', 'snowWrap'], ease: ['in', 'out'] },
  /** From the low guard the blade cut up high and forward, carried on over the head. */
  { id: 'snowRise', keys: ['lowGuard', 'snowRise', 'overhead'], ease: ['in', 'out'] },
  /** Raised high, brought down through the foe into the deepest lunge. */
  { id: 'snowCleave', keys: ['overhead', 'cut', 'snowCleave'], ease: ['in', 'out'] },
  /** Cocked back by the shoulder, driven straight through, drawn back. */
  { id: 'snowThrust', keys: ['qiGather', 'snowLunge', 'follow'], ease: ['in', 'out'] },
  /** The finisher: the falling cleave, the blade flicked clean, the finishing stance. */
  {
    id: 'snowFinale',
    keys: ['overhead', 'snowCleave', 'snowFlick', 'snowClean'],
    ease: ['in', 'out', 'inOut'],
  },
  /** The chain broken off before its finisher: out of the lunge, the blade flicked clean, settled. */
  { id: 'snowFinish', keys: ['snowCleave', 'snowFlick', 'snowClean'], ease: ['out', 'inOut'] },
  { id: 'recoverSnow', keys: ['snowClean', 'ready'], ease: ['inOut'] },
] as const satisfies readonly MotionDef[];

export const ARMS = {
  upcut: { hand: [11, 11], ang: -1.05, front: true },
  wrap: { hand: [10, 25], ang: Math.PI + 0.2, front: true },
  flick: { hand: [14, 35], ang: 0.8, front: true },
} satisfies Record<string, LimbSpec>;
